import { GodiverseLite } from '@apeiron/godiverse'
import { OrbitalSlot, Star } from '@apeiron/star'
import { NextImage } from '@apeiron/temp'
import { ApeironTooltip } from '@apeiron/ui'
import { Box, Button, styled, Typography } from '@mui/material'
import useGodiverseBuff from '@src/hooks/apeiron/godiverse/useGodiverseBuff'
import * as R from 'ramda'
import { FC, useMemo } from 'react'

const Container = styled(Box)`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  width: calc(44px * 5 + 10px * 4);
`

const Square = styled(Button)`
  align-items: center;
  aspect-ratio: 1;
  background-clip: content-box, border-box;
  background-image: linear-gradient(#161819, #161819),
    linear-gradient(#d2d4d6, #80807e, #d8dadc, #41474c, #9ea0a2);
  background-origin: border-box;
  border-radius: 2px;
  border: 2px solid transparent;
  display: flex;
  justify-content: center;
  min-width: unset;
  padding: unset;
  width: 44px;
`

const DimSquare = styled(Square)`
  border: 1px solid rgba(0, 0, 0, 0.6);
  pointer-events: none;
`

const Icon = styled(NextImage)`
  width: 90%;
  aspect-ratio: 1;
`

const Tooltip = ApeironTooltip

const TooltipContainer = styled(Box)`
  align-items: center;
  display: flex;
  gap: 5px;
`

const TooltipName = styled(Typography)`
  font-size: 14px;
  font-weight: 600;
  line-height: 14px;
`

const TooltipBuffName = styled(Typography)`
  font-size: 14px;
  font-weight: 400;
  line-height: 14px;
`

const TooltipBuff = styled(Typography)`
  font-size: 14px;
  font-weight: 400;
  line-height: 14px;
`

const AstronomicalTooltip: FC<TooltipProps> = (props: TooltipProps) => {
  const { astronomical, className, tooltipId } = props

  const { name } = astronomical

  const { getBuffConfig } = useGodiverseBuff()

  const config = useMemo(
    () => getBuffConfig(astronomical),
    [astronomical, getBuffConfig]
  )

  return (
    <Tooltip className={className} id={tooltipId}>
      <TooltipContainer>
        <TooltipName>{name}</TooltipName>
        <TooltipBuffName color={config.color.buffName}>
          {config.value.buffName}
        </TooltipBuffName>
        <TooltipBuff
          color={config.color.buff}
        >{`+${config.value.buff}`}</TooltipBuff>
      </TooltipContainer>
    </Tooltip>
  )
}

const AstronomicalItem: FC<ItemProps> = (props: ItemProps) => {
  const { astronomical, className, key } = props

  const tooltipId = `star-astronomical-slot-${astronomical.id}`

  return (
    <Square
      className={className}
      data-tooltip-id={tooltipId}
      key={key}
      aria-label='astronomical-slot'
    >
      <Icon src={astronomical.image} />
      <AstronomicalTooltip astronomical={astronomical} tooltipId={tooltipId} />
    </Square>
  )
}

const StarAstronomicalSlots: FC<Props> = props => {
  const { star, className } = props

  const astronomicals = useMemo(() => {
    const prepareAstronomicals = R.pipe<
      any,
      Array<OrbitalSlot>,
      Array<Array<GodiverseLite | null>>,
      Array<GodiverseLite | null>,
      Array<GodiverseLite | null>
    >(
      R.values,
      slots => R.pluck('equipped', slots),
      R.flatten,
      R.sort((a, b) => (R.isNil(b) ? -1 : 1))
    )

    return prepareAstronomicals(star.slotsMap)
  }, [star.slotsMap])

  return (
    <Container className={className}>
      {astronomicals.map(
        (astronomical: GodiverseLite | null, index: number) => {
          if (R.isNil(astronomical)) {
            const blankKey = `blank_${index}`

            return (
              <DimSquare aria-label='empty-astronomical-slot' key={blankKey} />
            )
          } else {
            return (
              <Box key={`${astronomical.id}_${index + 1}`}>
                <AstronomicalItem astronomical={astronomical} />
              </Box>
            )
          }
        },
        astronomicals
      )}
    </Container>
  )
}

type ItemProps = {
  astronomical: GodiverseLite
  className?: string
  key?: string
}

type TooltipProps = {
  astronomical: GodiverseLite
  className?: string
  tooltipId: string
}

type Props = {
  className?: string
  star: Star
}

export default StarAstronomicalSlots
